import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createEngagedTracker, screenPath, type Screen } from '@/lib/engagedTime';

/* SPEC-504 — engaged-time heartbeat. Mounted ONCE (AuthGate, beside usePageTracking).
 *
 * Posts `screen_engaged` to the existing POST /api/analytics/events every 15 s, counting only
 * while the tab is visible, the window is focused and there was input in the last 60 s (rules
 * in lib/engagedTime.ts). On hide/close the remainder is sent with `keepalive: true` — the
 * fetch form of navigator.sendBeacon, used because sendBeacon cannot carry the Authorization
 * header the endpoint requires. Sends the screen path (identifiers collapsed) and its `tab`
 * only: never key values, never other query parameters. Fire-and-forget. */

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const;

function send(screen: Screen, seconds: number, final: boolean) {
  const token = localStorage.getItem('suryaiq_token');
  if (!token) return;
  fetch('/api/analytics/events', {
    method: 'POST',
    keepalive: final,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      event_type: 'screen_engaged',
      page: screen.path,
      event_data: JSON.stringify({ seconds, tab: screen.tab }),
    }),
  }).catch(() => {});
}

export function useEngagedTime() {
  const location = useLocation();
  const trackerRef = useRef<ReturnType<typeof createEngagedTracker> | null>(null);

  useEffect(() => {
    const tracker = createEngagedTracker(
      {
        now: () => Date.now(),
        visible: () => document.visibilityState === 'visible',
        focused: () => document.hasFocus(),
      },
      send,
    );
    trackerRef.current = tracker;
    const onInput = () => tracker.input();
    const onVisibility = () => { if (document.visibilityState !== 'visible') tracker.hide(); };
    const onHide = () => tracker.hide();
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);
    const id = window.setInterval(() => tracker.tick(), 1000);
    return () => {
      window.clearInterval(id);
      tracker.hide();
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      trackerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    trackerRef.current?.setScreen({ path: screenPath(location.pathname), tab: tab ? tab.slice(0, 100) : null });
  }, [location.pathname, location.search]);
}
